import Highcharts from 'highcharts';
import { useMemo } from 'react';
import { useTheme } from '../../../../../../theme/theme';
import { AsyncComponentHookResult } from '../../../../../../Types/ComponentHookResult';
import { useIsPdf } from '../../../../../hooks/useIsPdf';
import { useGoal } from '../../../../hooks/useGoal';
import { useRetirementIncomeColors } from './hooks/useRetirementIncomeColors';
import { RetirementIncomeChartDTO } from './RetirementIncome.types';

const seriesList = [
  {
    key: 'incomeShortage',
    name: 'Total Income Shortage',
  },
  {
    key: 'otherIncome',
    name: 'Other',
  },
  {
    key: 'distributions',
    name: 'Distributions',
  },
  {
    key: 'annuity',
    name: 'Annuity',
  },
  {
    key: 'pension',
    name: 'Pension',
  },
  {
    key: 'socialSecurity',
    name: 'Social Security',
  },
];

export type ValuesKeys =
  | 'incomeShortage'
  | 'distributions'
  | 'otherIncome'
  | 'annuity'
  | 'pension'
  | 'socialSecurity'
  | 'totalGuaranteedIncome'
  | 'livingExpenses'
  | 'guaranteedIncomeShortage';

export type Values = Record<ValuesKeys, number[]>;

interface Series {
  name: string;
  data: number[];
  color: string;
  borderColor: string;
}

interface UseRetirementIncomeData {
  values: Values;
  series: Series[];
  categories: string[];
  customSettings: Highcharts.Options;
  isPdf: boolean;
}

interface UseRetirementIncomeParams {
  goalId?: string;
  onChartClick(index: number): void;
}

export const useRetirementIncome = ({
  onChartClick,
  goalId,
}: UseRetirementIncomeParams): AsyncComponentHookResult<UseRetirementIncomeData> => {
  const isPdf = useIsPdf();

  const queryResult = useGoal({ goalId });

  const theme = useTheme();

  const retirementIncomeColors = useRetirementIncomeColors();

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const goal = queryResult.data;

    if (!goal) {
      return null;
    }

    const { GoalCharts } = goal;

    if (!GoalCharts) {
      return null;
    }

    const chartData = GoalCharts[1] as RetirementIncomeChartDTO;

    if (!chartData) {
      return null;
    }

    const { Data } = chartData;

    const categories = [] as string[];

    const values: Values = {
      incomeShortage: [],
      distributions: [],
      otherIncome: [],
      annuity: [],
      pension: [],
      socialSecurity: [],
      totalGuaranteedIncome: [],
      livingExpenses: [],
      guaranteedIncomeShortage: [],
    };

    Data.forEach(point => {
      categories.push(point.Category);
      values.incomeShortage.push(Math.abs(point.IncomeShortage));
      values.distributions.push(point.Distributions);
      values.otherIncome.push(point.OtherIncome);
      values.annuity.push(point.Annuity);
      values.pension.push(point.Pension);
      values.socialSecurity.push(point.SocialSecurity);
      values.guaranteedIncomeShortage.push(Math.abs(point.GuaranteedIncomeShortage));
      values.totalGuaranteedIncome.push(point.TotalGuaranteedIncome);
      values.livingExpenses.push(point.RetirementExpenses);
    });

    const series: Series[] = seriesList.map(({ key, name }) => ({
      name,
      data: values[key],
      color: retirementIncomeColors[key].bg,
      borderColor: retirementIncomeColors[key].border,
    }));

    const customSettings: Highcharts.Options = {
      plotOptions: {
        series: {
          events: {
            click(evt) {
              onChartClick(evt.point.index);
            },
          },
        },
      },

      xAxis: {
        tickInterval: 3,
        tickWidth: 0,
      },

      tooltip: {
        borderRadius: 10,
        shadow: false,
        useHTML: true,
        backgroundColor: theme.colors.neutral['700'],
        borderColor: theme.colors.neutral['700'],
        shape: 'square',
        style: {
          color: theme.colors.neutral['100'],
        },
        formatter() {
          return `
            <div style='display: flex; align-items: flex-start;'>
              <div style='width: 8px; height: 8px; border-radius: 4px; margin-top: 5px; margin-right: 4px; background: ${
                (this as any).series.color
              }'></div>
              
              <div style='font-size: 12px; color: ${theme.colors.neutral['100']};'>
                <span style='margin-right: 4px;'>
                  ${this.series.name}:
                </span>
                <strong>
                  Click for details
                </strong>
              </div>
            </div>
          `;
        },
      },
    };

    return {
      series,
      values,
      categories,
      customSettings,
      isPdf,
    };
  }, [onChartClick, queryResult.data, retirementIncomeColors, theme, isPdf]);

  return {
    ...queryResult,
    data,
  };
};
